import "./PollyReOpenPage.scss";

import { Button, Flex, Text } from "@fluentui/react-northstar";
import { dialog } from "@microsoft/teams-js";
import FormState, { FieldDescriptor } from "@shopify/react-form-state";
import { getSurveyTypeDisplay } from "@teams/cards";
import { SurveyDTO } from "@teams/types/api";
import moment from "moment";
import { useMemo, useState } from "react";

import ConfirmationImage from "../../../_shared/assets/confirmation-image-ok-mark.svg";
import { DropdownField } from "../../../_shared/components/fields";
import { PollyDialog } from "../../../_shared/components/PollyDialog";
import { surveyApi } from "../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { SchedulePollyField } from "./SchedulePollyField";
import { SettingLabel } from "./SettingLabel";

type Props = {
  survey: SurveyDTO;
};

type CloseStrategy = "manual" | "scheduled";
export const PollyReOpenPage: React.FC<Props> = ({ survey }) => {
  const { teamsContext } = useTeamsContext();
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const initialFields = useMemo(
    () =>
      ({ closeAt: undefined, rrule: "", closeStrategySelection: "manual" } as {
        closeAt: Date | undefined;
        rrule: string;
        closeStrategySelection: CloseStrategy;
      }),
    []
  );

  return (
    <>
      <FormState
        initialValues={initialFields}
        onSubmit={async ({ fields }) => {
          const closeAt = fields.closeAt.value;
          setSubmitting(true);
          await surveyApi.reopenSurvey(teamsContext, survey.id!, closeAt);
          setSubmitting(false);
          setShowSuccess(true);
        }}
      >
        {({ fields, submit }) => {
          const showDialog = () => {
            dialog.update.resize({ width: 550, height: 430 });
            setShowDateTimePicker(true);
          };

          const hideDialog = (withTime = false) => {
            if (!withTime) {
              fields.closeStrategySelection.onChange("manual");
            }
            dialog.update.resize({ width: 550, height: 240 });
            setShowDateTimePicker(false);
          };

          return (
            <>
              <Flex column className="polly-reopen-dialog">
                {showSuccess ? (
                  <>
                    <Flex fill column hAlign="center" gap="gap.small">
                      <img src={ConfirmationImage} height={80} alt="Success" />
                      <Text
                        size="large"
                        weight="semibold"
                        content={`${getSurveyTypeDisplay(survey.type, {
                          capitalize: true,
                        })} reopened!`}
                      />
                      <Text
                        content={`Participants will be notified to respond to this ${getSurveyTypeDisplay(
                          survey.type
                        )}.`}
                      />
                    </Flex>
                  </>
                ) : (
                  <>
                    <Flex gap="gap.medium" column>
                      <Text
                        content={`Choose how long you would like to keep this ${getSurveyTypeDisplay(
                          survey.type
                        )} open`}
                        size="medium"
                      />
                      <Flex column>
                        <Flex gap="gap.small">
                          <SettingLabel icon="clock-icon" content={"Close polly"} />
                          <DropdownField
                            className="reopen-dropdown"
                            field={fields.closeStrategySelection}
                            selectedItemLabel={item => {
                              if (item.value === "manual") {
                                hideDialog();
                                fields.closeAt.onChange(undefined);
                              } else {
                                if (!fields.closeAt.value) {
                                  fields.closeAt.onChange(new Date());
                                  showDialog();
                                }
                              }
                              return item.label;
                            }}
                            items={[
                              { label: "Manual Close", value: "manual" },
                              { label: "Schedule", value: "scheduled" },
                            ]}
                            value={
                              fields.closeAt.value
                                ? `on ${moment(fields.closeAt.value).format("lll")}`
                                : "Manual Close"
                            }
                            fluid
                          />
                        </Flex>
                        <Flex hAlign="end" style={{ marginTop: "4rem" }} gap="gap.small">
                          <Button
                            content={"Cancel"}
                            onClick={() => {
                              dialog.url.submit();
                            }}
                          />
                          <Button
                            primary
                            content={`Reopen ${getSurveyTypeDisplay(survey.type)}`}
                            onClick={() => {
                              submit();
                            }}
                            disabled={submitting}
                            loading={submitting}
                          />
                        </Flex>
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>

              <PollyDialog width="90vw" open={showDateTimePicker} onCancel={() => hideDialog()}>
                <Flex column gap="gap.large">
                  <SchedulePollyField
                    rrule={fields.rrule}
                    sendAt={fields.closeAt as FieldDescriptor<Date>}
                    column
                    rruleRequired={false}
                    isCloseTime={true}
                    isEdit={true}
                  />
                  <Flex style={{ marginTop: "12rem" }} hAlign="end" gap="gap.small">
                    <Button
                      content={"Cancel"}
                      onClick={async () => {
                        fields.closeStrategySelection.onChange("manual");
                        hideDialog();
                      }}
                    />
                    <Button
                      primary
                      content={"Save"}
                      onClick={async () => {
                        hideDialog(true);
                      }}
                      disabled={submitting}
                      loading={submitting}
                    />
                  </Flex>
                </Flex>
              </PollyDialog>
            </>
          );
        }}
      </FormState>
    </>
  );
};
